"use strict";
/**************************************************************************************************
 *
 *      @file ./extend-kotlin-array
 *
 *      (Not Javelin-specific - this is usable in any KotlinJS project)
 *
 *      Gives KotlinJS array objects a set of methods & properties that allows
 *      them to also operate as regular JS arrays. Without this, KotlinJS array
 *      objects can't be used as arrays in regular JS code (and vice versa).
 *
 *      Added methods & properties are those present in normal JS objects (this
 *      polyfill emulates their functionality). Turns KotlinJS arrays into
 *      fully cross-platform array objects that can also be used normally in
 *      regular JS code without data conversion & type issues.
 *
 *      Added methods:
 *         - clone, push, unshift, pop, shift, join, forEach, map, reduce,
 *           reduceRight, filter, find, findIndex, every, some, reverse,
 *           append, concat, slice, sortClone, sort, sortNumeric
 *
 *      Added properties:
 *         - array
 *             * proxies to the internal array property)
 *         - length
 *             * getter that calculates the length of the internal array property)
 *         - array indexes up to 100 (0, 1, 2, ..., 100)
 *             *  e.g. kArrayInstance[0] returns 1st item in array kArrayInstance
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/********************************************* CONFIG *********************************************/
var add1ArgHashedKey = 'add_11rb$';
var addMultiArgHashedKey = 'add_wxm5ur$';
var removeAtHashedKey = 'removeAt_za3lpa$';
var getKey = 'get_za3lpa$';
/**************************************** INTERNAL HELPERS ****************************************/
// Memoization location for arrayHashedKey
var arrayHashedKey;
var getArrayHashedKey = function (arr) {
    if (arrayHashedKey)
        return arrayHashedKey;
    arrayHashedKey = Object.keys(arr).find(function (key) { return !!key.match(/^array_/g); });
    return arrayHashedKey;
};
function _isInnerArraySet(kotlinArr) {
    return (typeof kotlinArr[getArrayHashedKey(kotlinArr)] !== 'undefined' &&
        kotlinArr[getArrayHashedKey(kotlinArr)] != null);
}
/************************************** THIRD-PARTY MODULES ***************************************/
var kotlin_1 = __importDefault(require("kotlin"));
var shared_1 = require("mad-utils/lib/shared");
var KotlinArrayList = kotlin_1.default.kotlin.collections.ArrayList;
exports.KotlinArrayList = KotlinArrayList;
/***************************** KOTLIN ArrayList CONVENIENCE FUNCTIONS *****************************/
shared_1.defineMethod(KotlinArrayList.prototype, 'clone', function clone(withModCount) {
    if (withModCount === void 0) { withModCount = false; }
    var dup = new KotlinArrayList(this[getArrayHashedKey(this)].slice(0));
    if (withModCount)
        dup.modCount = this.modCount;
    return dup;
});
/******************* KOTLIN ArrayList CONVENIENCE FUNCTIONS AND PROP REMAPPINGS *******************/
if (!KotlinArrayList.prototype.array) {
    shared_1.defineGetter(KotlinArrayList.prototype, 'array', function getArray() {
        return this[getArrayHashedKey(this)];
    });
}
shared_1.defineGetter(KotlinArrayList.prototype, 'length', function getLength() {
    return (this[getArrayHashedKey(this)] && this[getArrayHashedKey(this)].length) || 0;
});
shared_1.defineMethod(KotlinArrayList.prototype, 'add', function add(el, idx) {
    if (!_isInnerArraySet(this))
        this[getArrayHashedKey(this)] = [];
    if (typeof idx === 'undefined')
        return this[add1ArgHashedKey](el);
    return this[addMultiArgHashedKey](idx, el);
});
shared_1.defineMethod(KotlinArrayList.prototype, 'get', function get(idx) {
    return this[getArrayHashedKey(this)] && this[getKey](idx);
});
var _loop_1 = function (idx) {
    Object.defineProperty(KotlinArrayList.prototype, idx.toString(), {
        enumerable: true,
        configurable: false,
        get: function getIdx() {
            if (typeof this[getArrayHashedKey(this)] === 'undefined')
                return;
            return this[getArrayHashedKey(this)][idx];
        },
    });
};
/**
 * Add index properties (allows for arrList[0], ..., arrList[100])
 */
for (var idx = 0; idx <= 200; idx++) {
    _loop_1(idx);
}
/************************** PATCH JS Array METHODS INTO KOTLIN ArrayList **************************/
shared_1.defineMethod(KotlinArrayList.prototype, 'push', function push(el) {
    if (typeof this[getArrayHashedKey(this)] === 'undefined')
        this[getArrayHashedKey(this)] = [];
    this[add1ArgHashedKey](el);
    return this.size; // Return size to mimic regular array behaviour
});
shared_1.defineMethod(KotlinArrayList.prototype, 'unshift', function unshift(el) {
    if (typeof this[getArrayHashedKey(this)] === 'undefined')
        this[getArrayHashedKey(this)] = [];
    this[addMultiArgHashedKey](0, el);
    return this.size; // Return size to mimic regular array behaviour
});
shared_1.defineMethod(KotlinArrayList.prototype, 'pop', function pop() {
    if (_isInnerArraySet(this)) {
        return this[removeAtHashedKey](this[getArrayHashedKey(this)].length - 1);
    }
});
shared_1.defineMethod(KotlinArrayList.prototype, 'shift', function shift() {
    if (_isInnerArraySet(this))
        return this[removeAtHashedKey](0);
});
shared_1.defineMethod(KotlinArrayList.prototype, 'join', function join(char) {
    return _isInnerArraySet(this) ? this[getArrayHashedKey(this)].join(char) : '';
});
shared_1.defineMethod(KotlinArrayList.prototype, 'forEach', function forEach(predicate) {
    var args = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        args[_i - 1] = arguments[_i];
    }
    var _a;
    if (!_isInnerArraySet(this))
        return;
    return (_a = this[getArrayHashedKey(this)]).forEach.apply(_a, [predicate].concat(args));
});
shared_1.defineMethod(KotlinArrayList.prototype, 'map', function map(predicate) {
    var args = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        args[_i - 1] = arguments[_i];
    }
    var _a;
    if (!_isInnerArraySet(this))
        return [];
    return (_a = this[getArrayHashedKey(this)]).map.apply(_a, [predicate].concat(args));
});
shared_1.defineMethod(KotlinArrayList.prototype, 'reduce', function reduce(predicate, defAccVal) {
    var args = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        args[_i - 2] = arguments[_i];
    }
    var _a;
    return (_a = (_isInnerArraySet(this) ? this[getArrayHashedKey(this)] : [])).reduce.apply(_a, [predicate, defAccVal].concat(args));
});
shared_1.defineMethod(KotlinArrayList.prototype, 'reduceRight', function reduceRight(predicate, defAccVal) {
    var args = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        args[_i - 2] = arguments[_i];
    }
    var _a;
    return (_a = (_isInnerArraySet(this) ? this[getArrayHashedKey(this)] : [])).reduceRight.apply(_a, [predicate, defAccVal].concat(args));
});
shared_1.defineMethod(KotlinArrayList.prototype, 'filter', function filter(predicate, thisArg) {
    if (!_isInnerArraySet(this))
        return [];
    return thisArg
        ? this[getArrayHashedKey(this)].filter(predicate, thisArg)
        : this[getArrayHashedKey(this)].filter(predicate);
});
shared_1.defineMethod(KotlinArrayList.prototype, 'find', function find(predicate, thisArg) {
    if (!_isInnerArraySet(this))
        return;
    return thisArg
        ? this[getArrayHashedKey(this)].find(predicate, thisArg)
        : this[getArrayHashedKey(this)].find(predicate);
});
shared_1.defineMethod(KotlinArrayList.prototype, 'findIndex', function findIndex(predicate, thisArg) {
    if (!_isInnerArraySet(this))
        return -1;
    return thisArg
        ? this[getArrayHashedKey(this)].findIndex(predicate, thisArg)
        : this[getArrayHashedKey(this)].findIndex(predicate);
});
shared_1.defineMethod(KotlinArrayList.prototype, 'every', function every(predicate, thisArg) {
    if (!_isInnerArraySet(this))
        return true; // <- To mimic behaviour of Array.prototype.every.
    return thisArg
        ? this[getArrayHashedKey(this)].every(predicate, thisArg)
        : this[getArrayHashedKey(this)].every(predicate);
});
shared_1.defineMethod(KotlinArrayList.prototype, 'some', function some(predicate, thisArg) {
    if (!_isInnerArraySet(this))
        return false; // <- To mimic behaviour of Array.prototype.some.
    return thisArg
        ? this[getArrayHashedKey(this)].some(predicate, thisArg)
        : this[getArrayHashedKey(this)].some(predicate);
});
shared_1.defineMethod(KotlinArrayList.prototype, 'reverse', function reverse() {
    if (!_isInnerArraySet(this))
        return;
    this[getArrayHashedKey(this)].reverse();
    return this;
});
/**
 * Mutatively add items to inner array.
 */
shared_1.defineMethod(KotlinArrayList.prototype, 'append', function append(coll) {
    if (!_isInnerArraySet(this))
        this[getArrayHashedKey(this)] = [];
    if (coll instanceof KotlinArrayList && coll[getArrayHashedKey(coll)]) {
        this.modCount++;
        this[getArrayHashedKey(this)] = this[getArrayHashedKey(this)].concat(coll[getArrayHashedKey(coll)]);
    }
    else if (Array.isArray(coll)) {
        this.modCount++;
        this[getArrayHashedKey(this)] = this[getArrayHashedKey(this)].concat(coll);
    }
    else if (arguments.length > 0) {
        this.push(coll);
    }
    return this;
});
/**
 * Immutably add items to inner array (i.e. return a new KotlinArrayList)
 */
shared_1.defineMethod(KotlinArrayList.prototype, 'concat', function concat(coll) {
    var dup = this.clone(true);
    var retVal = arguments.length > 0 ? dup.append(coll) : dup.append();
    return retVal;
});
/**
 * Immutably add items to inner array (i.e. return a new KotlinArrayList)
 */
shared_1.defineMethod(KotlinArrayList.prototype, 'slice', function slice(begin, end) {
    if (arguments.length === 0)
        return this.clone(true);
    return new KotlinArrayList(this[getArrayHashedKey(this)].slice(begin, end));
});
/**
 * Immutable sort
 */
shared_1.defineMethod(KotlinArrayList.prototype, 'sortClone', function sortClone(compareFn) {
    var dup = this.clone(true);
    if (arguments.length === 0)
        dup[getArrayHashedKey(dup)].sort();
    if (arguments.length >= 1)
        dup[getArrayHashedKey(dup)].sort(compareFn);
    return dup;
});
/**
 * Standard mutable sort (MUTATIVE)
 */
shared_1.defineMethod(KotlinArrayList.prototype, 'sort', function sort(compareFn) {
    this[getArrayHashedKey(this)].sort();
    return this;
});
/**
 * Standard mutable sort, with proper number sorting (MUTATIVE)
 */
shared_1.defineMethod(KotlinArrayList.prototype, 'sortNumeric', function sortNumeric(compareFn) {
    this[getArrayHashedKey(this)].sort(function (a, b) { return a - b; });
    return this;
});
shared_1.defineMethod(KotlinArrayList.prototype, 'splice', function splice() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    var _a;
    return (_a = this.array).splice.apply(_a, args);
});
//# sourceMappingURL=extend-kotlin-array.js.map